import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { 
  DoorOpen, AlertTriangle, Briefcase, Building2, 
  Calendar, FileSignature, CheckCircle2, Info, Upload, FileText, X
} from 'lucide-react';
import SignaturePad from './SignaturePad';
import api from '../services/api';

const MOTIFS_SORTIE = [
  { value: 'Abandon', label: 'Abandon', needsContract: false, needsSiret: false },
  { value: 'Emploi ou Intérim > 6 mois', label: 'Emploi ou Intérim > 6 mois', needsContract: true, needsSiret: false },
  { value: 'Emploi ou Intérim < 6 mois', label: 'Emploi ou Intérim < 6 mois', needsContract: true, needsSiret: false },
  { value: 'Formation', label: 'Formation', needsContract: false, needsSiret: false },
  { value: 'Création d\'entreprise', label: 'Création d\'entreprise', needsContract: false, needsSiret: true }
];

const SortieVolontaire = () => {
  const [existingSortie, setExistingSortie] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [entreprises, setEntreprises] = useState([]);
  const signatureRef = useRef(null);
  const fileInputRef = useRef(null);
  const [justificatifFile, setJustificatifFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  const [formData, setFormData] = useState({
    motif: '',
    date_sortie: '',
    commentaire: '',
    // Emploi
    contrat_entreprise_id: '',
    contrat_type: '',
    contrat_date_signature: '',
    contrat_salaire: '',
    contrat_horaires: '',
    // Création entreprise
    entreprise_date_creation: '',
    entreprise_siret: '',
    // Justificatif
    justificatif_id: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [sortieRes, entreprisesRes] = await Promise.all([
        api.get('/sortie-volontaire/me'),
        api.get('/entreprises')
      ]);
      
      setExistingSortie(sortieRes.data);
      setEntreprises(entreprisesRes.data || []);
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMotifInfo = (motif) => MOTIFS_SORTIE.find(m => m.value === motif);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Vérifier la taille (max 10 Mo)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Le fichier ne doit pas dépasser 10 Mo');
      return;
    }

    // Types acceptés
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non autorisé. Formats acceptés : PDF, Word, Images');
      return;
    }

    setUploadingFile(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('type', 'justificatif_sortie');

      const response = await api.post('/upload-justificatif', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setJustificatifFile({ name: file.name, id: response.data.id });
      setFormData(prev => ({ ...prev, justificatif_id: response.data.id }));
      toast.success('Justificatif téléversé');
    } catch (error) {
      toast.error('Erreur lors du téléversement');
    } finally {
      setUploadingFile(false);
    }
  };

  const removeJustificatif = () => {
    setJustificatifFile(null);
    setFormData(prev => ({ ...prev, justificatif_id: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!formData.motif || !formData.date_sortie) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    const motifInfo = getMotifInfo(formData.motif);
    
    // Vérifications selon le motif
    if (motifInfo?.needsContract && !formData.contrat_entreprise_id) {
      toast.error('Veuillez sélectionner une entreprise pour le contrat');
      return;
    }
    
    if (motifInfo?.needsSiret && !formData.entreprise_siret) {
      toast.error('Veuillez renseigner le numéro SIRET');
      return;
    }

    // Récupérer la signature
    const signatureData = signatureRef.current?.save();
    if (!signatureData || signatureData === 'signed') {
      toast.error('Veuillez signer le formulaire');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/sortie-volontaire', {
        ...formData,
        signature: signatureData,
        contrat_salaire: formData.contrat_salaire ? parseFloat(formData.contrat_salaire) : null
      });
      
      toast.success('Demande de sortie enregistrée. Les notifications ont été envoyées.');
      setShowDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-slate-500">Chargement...</p>
        </CardContent>
      </Card>
    );
  }

  // Affichage si une demande existe déjà
  if (existingSortie) {
    return (
      <Card className="bg-amber-50 border-amber-200 rounded-3xl shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-700">
            <DoorOpen className="w-6 h-6" />
            Demande de Sortie du Dispositif
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-white rounded-xl p-4 border border-amber-200">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-slate-500">Motif</span>
                <p className="font-medium text-slate-900">{existingSortie.motif}</p>
              </div>
              <div>
                <span className="text-sm text-slate-500">Date de sortie</span>
                <p className="font-medium text-slate-900">{existingSortie.date_sortie}</p>
              </div>
              <div>
                <span className="text-sm text-slate-500">Statut</span>
                <Badge className={
                  existingSortie.statut === 'validee' ? 'bg-emerald-100 text-emerald-700' :
                  existingSortie.statut === 'refusee' ? 'bg-red-100 text-red-700' :
                  'bg-amber-100 text-amber-700'
                }>
                  {existingSortie.statut === 'validee' ? 'Validée' :
                   existingSortie.statut === 'refusee' ? 'Refusée' : 'En attente'}
                </Badge>
              </div>
              {existingSortie.notifications_envoyees && (
                <div>
                  <span className="text-sm text-slate-500">Notifications</span>
                  <Badge className="bg-emerald-100 text-emerald-700">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Envoyées
                  </Badge>
                </div>
              )}
            </div>
            
            {existingSortie.commentaire && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <span className="text-sm text-slate-500">Commentaire</span>
                <p className="text-slate-700">{existingSortie.commentaire}</p>
              </div>
            )}
            
            {existingSortie.entreprise_siret && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <span className="text-sm text-slate-500">SIRET de votre entreprise</span>
                <p className="font-mono text-slate-900">{existingSortie.entreprise_siret}</p>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2 text-amber-700 bg-amber-100 rounded-xl p-3">
            <Info className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">
              Votre demande a été signée électroniquement et transmise à votre référent et aux intervenants.
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const motifInfo = getMotifInfo(formData.motif);

  return (
    <>
      <Card className="bg-white rounded-3xl shadow-sm border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="flex items-center gap-2">
            <DoorOpen className="w-6 h-6 text-slate-600" />
            Sortie du Dispositif
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <DoorOpen className="w-8 h-8 text-slate-500" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Vous souhaitez quitter le dispositif ?
            </h3>
            <p className="text-slate-500 mb-6 max-w-md mx-auto">
              Cette démarche est définitive. Elle notifiera automatiquement votre référent et les intervenants.
            </p>
            <Button
              onClick={() => setShowDialog(true)}
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50 rounded-xl"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Demander une sortie volontaire
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Dialog de sortie */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <DoorOpen className="w-6 h-6 text-amber-600" />
              Demande de Sortie Volontaire
            </DialogTitle>
            <DialogDescription>
              Complétez le formulaire et signez pour confirmer votre sortie du dispositif.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Motif de sortie */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">Motif de sortie *</Label>
              <Select 
                value={formData.motif}
                onValueChange={(val) => setFormData({ ...formData, motif: val })}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Sélectionnez un motif" />
                </SelectTrigger>
                <SelectContent>
                  {MOTIFS_SORTIE.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date de sortie */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">Date de sortie souhaitée *</Label>
              <Input
                type="date"
                value={formData.date_sortie}
                onChange={(e) => setFormData({ ...formData, date_sortie: e.target.value })}
                className="rounded-xl"
              />
            </div>

            {/* Si Emploi - Formulaire contrat */}
            {motifInfo?.needsContract && (
              <div className="bg-emerald-50 rounded-xl p-4 space-y-4 border border-emerald-200">
                <div className="flex items-center gap-2 text-emerald-700 font-semibold">
                  <Briefcase className="w-5 h-5" />
                  Informations sur le nouveau contrat
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Entreprise *</Label>
                    <Select 
                      value={formData.contrat_entreprise_id}
                      onValueChange={(val) => setFormData({ ...formData, contrat_entreprise_id: val })}
                    >
                      <SelectTrigger className="rounded-xl bg-white">
                        <SelectValue placeholder="Sélectionnez" />
                      </SelectTrigger>
                      <SelectContent>
                        {entreprises.map(e => (
                          <SelectItem key={e.id} value={e.id}>{e.nom}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Type de contrat</Label>
                    <Select 
                      value={formData.contrat_type}
                      onValueChange={(val) => setFormData({ ...formData, contrat_type: val })}
                    >
                      <SelectTrigger className="rounded-xl bg-white">
                        <SelectValue placeholder="Sélectionnez" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CDI">CDI</SelectItem>
                        <SelectItem value="CDD">CDD</SelectItem>
                        <SelectItem value="Intérim">Intérim</SelectItem>
                        <SelectItem value="Alternance">Alternance</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Date de signature</Label>
                    <Input
                      type="date"
                      value={formData.contrat_date_signature}
                      onChange={(e) => setFormData({ ...formData, contrat_date_signature: e.target.value })}
                      className="rounded-xl bg-white"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Salaire brut mensuel (€)</Label>
                    <Input
                      type="number"
                      value={formData.contrat_salaire}
                      onChange={(e) => setFormData({ ...formData, contrat_salaire: e.target.value })}
                      placeholder="1800"
                      className="rounded-xl bg-white"
                    />
                  </div>
                  
                  <div className="col-span-2 space-y-2">
                    <Label>Horaires</Label>
                    <Input
                      value={formData.contrat_horaires}
                      onChange={(e) => setFormData({ ...formData, contrat_horaires: e.target.value })}
                      placeholder="Ex: Temps plein, 35h/semaine"
                      className="rounded-xl bg-white"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Si Création entreprise */}
            {motifInfo?.needsSiret && (
              <div className="bg-blue-50 rounded-xl p-4 space-y-4 border border-blue-200">
                <div className="flex items-center gap-2 text-blue-700 font-semibold">
                  <Building2 className="w-5 h-5" />
                  Informations sur votre entreprise
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date de création *</Label>
                    <Input
                      type="date"
                      value={formData.entreprise_date_creation}
                      onChange={(e) => setFormData({ ...formData, entreprise_date_creation: e.target.value })}
                      className="rounded-xl bg-white"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Numéro SIRET *</Label>
                    <Input
                      value={formData.entreprise_siret}
                      onChange={(e) => setFormData({ ...formData, entreprise_siret: e.target.value })}
                      placeholder="XXX XXX XXX XXXXX"
                      className="rounded-xl bg-white font-mono"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Commentaire */}
            <div className="space-y-2">
              <Label>Commentaire (optionnel)</Label>
              <Textarea
                value={formData.commentaire}
                onChange={(e) => setFormData({ ...formData, commentaire: e.target.value })}
                placeholder="Précisions sur votre sortie..."
                className="rounded-xl min-h-[80px]"
              />
            </div>

            {/* Téléversement de justificatif */}
            <div className="space-y-3">
              <Label className="text-base font-semibold flex items-center gap-2">
                <Upload className="w-5 h-5 text-slate-600" />
                Justificatif (optionnel)
              </Label>
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-4 bg-slate-50">
                {!justificatifFile ? (
                  <div className="text-center">
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileSelect}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      className="hidden"
                      id="justificatif-upload"
                    />
                    <label
                      htmlFor="justificatif-upload"
                      className="cursor-pointer"
                    >
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
                          <Upload className="w-6 h-6 text-slate-500" />
                        </div>
                        <p className="text-sm text-slate-600">
                          {uploadingFile ? 'Téléversement...' : 'Cliquez pour téléverser un justificatif'}
                        </p>
                        <p className="text-xs text-slate-400">
                          PDF, Word ou Images (max 10 Mo)
                        </p>
                      </div>
                    </label>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-slate-200">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <span className="text-sm font-medium text-slate-700 truncate max-w-[200px]">
                        {justificatifFile.name}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={removeJustificatif}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-500">
                Vous pouvez joindre un document justificatif (contrat de travail, attestation, etc.)
              </p>
            </div>

            {/* Zone de signature */}
            <div className="space-y-3">
              <Label className="text-base font-semibold flex items-center gap-2">
                <FileSignature className="w-5 h-5 text-slate-600" />
                Signature électronique *
              </Label>
              <div className="border-2 border-dashed border-slate-300 rounded-xl p-4">
                <SignaturePad ref={signatureRef} />
              </div>
              <p className="text-xs text-slate-500">
                En signant ce formulaire, je confirme ma volonté de quitter le dispositif et je comprends que cette démarche est définitive.
              </p>
            </div>
          </div>

          <DialogFooter className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowDialog(false)}
              className="rounded-xl"
            >
              Annuler
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-amber-600 hover:bg-amber-700 rounded-xl"
            >
              {submitting ? 'Envoi en cours...' : 'Confirmer et Signer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SortieVolontaire;
